import React from 'react';
import { Info, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { primaryText } from '../../styling/stylingHelper';

export type CalloutTone = 'info' | 'warning' | 'success';

export interface CalloutProps {
  text: string;
  tone?: CalloutTone;
  className?: string;
}

/**
 * Tiny inline emphasis banner (info / warning / success). Used by the Cloud Agent
 * renderer for prose `callout` blocks. Variants use the standard tint palette via a
 * left accent border + soft tinted background.
 */
const TONE_STYLES: Record<CalloutTone, { container: string; icon: string; Icon: typeof Info }> = {
  info: {
    container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-500',
    icon: 'text-blue-500',
    Icon: Info,
  },
  warning: {
    container: 'bg-amber-50 dark:bg-amber-900/20 border-amber-500',
    icon: 'text-amber-500',
    Icon: AlertTriangle,
  },
  success: {
    container: 'bg-green-50 dark:bg-green-900/20 border-green-500',
    icon: 'text-green-600',
    Icon: CheckCircle,
  },
};

export const Callout: React.FC<CalloutProps> = ({ text, tone = 'info', className }) => {
  const { container, icon, Icon } = TONE_STYLES[tone];
  return (
    <div
      className={cn(
        'flex items-start gap-3 border-l-4 rounded p-4',
        container,
        className,
      )}
    >
      <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', icon)} />
      <p className={cn(primaryText, 'text-sm leading-5')}>{text}</p>
    </div>
  );
};
